import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzModalRef, NzModalModule, NZ_MODAL_DATA, NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { FormsModule } from '@angular/forms';
import { ProductionOrderProcessesService } from 'src/app/private/services/production-order-processes.service';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { RawMaterialDto } from 'src/app/shared/dto/rawMaterial.dto';
import { RawMaterialToUseDto } from 'src/app/shared/dto/raw-material-to-use.dto';
import { ProductionOrderProcessDto } from 'src/app/shared/dto/production-order-process.dto';

@Component({
    selector: 'app-modal-apply-temp-loaded-raw-materials',
    standalone: true,
    imports: [CommonModule, NzTableModule, NzButtonModule, NzCheckboxModule, NzModalModule, FormsModule],
    templateUrl: './modal-apply-temp-loaded-raw-materials.component.html'
})
export class ModalApplyTempLoadedRawMaterialsComponent implements OnInit {
    nzModalRef = inject(NzModalRef);
    private readonly modalService = inject(NzModalService);
    private readonly productionOrderProcessesService = inject(ProductionOrderProcessesService);
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly message = inject(NzMessageService);
    readonly data? = inject(NZ_MODAL_DATA);

    machineId!: number;
    orderProcessId!: number;

    items: any[] = [];
    loading: boolean = false;
    allowPreload: boolean = true;

    async ngOnInit(): Promise<void> {
        this.machineId = this.data?.machineId;
        this.orderProcessId = this.data?.orderProcessId;

        await this.loadItems();
        await this.validatePreloadCompatibility();
    }

    private async loadItems(): Promise<void> {
        if (!this.machineId || this.machineId <= 0) {
            this.items = [];
            return;
        }

        this.loading = true;
        try {
            const tempLoadedItems: any[] = await this.productionOrderProcessesService.getTempLoadedRawMaterialsByMachine(this.machineId);
            // Añadir campo local "preload" por defecto false
            this.items = tempLoadedItems.map((tempItem: any) => ({ ...tempItem, preload: false }));
        } catch (e: any) {
            this.message.error(`Error cargando materias primas temporales: ${e.message}`);
        } finally {
            this.loading = false;
        }
    }

    private async validatePreloadCompatibility(): Promise<void> {
        try {
            if (!this.items || this.items.length === 0) {
                return;
            }

            if (!this.orderProcessId || this.orderProcessId <= 0) {
                return;
            }

            // Obtener conteo de artículos de las materias primas temporales (mapa: articleId -> cantidad)
            const tempArticleCountsById = new Map<number, number>();
            this.items.forEach((tempItem: any) => {
                const articleId = tempItem?.rawMaterialUsed?.article?.id ?? tempItem?.rawMaterialToUse?.articleId;
                if (articleId && articleId > 0) tempArticleCountsById.set(articleId, (tempArticleCountsById.get(articleId) ?? 0) + 1);
            });

            // Obtener materias primas a usar del proceso actual y su conteo (mapa: articleId -> cantidad)
            let existingOrderProcess: ProductionOrderProcessDto | null = null;
            try {
                existingOrderProcess = await this.productionOrderProcessesService.getById(this.orderProcessId) as ProductionOrderProcessDto;
            } catch (err: any) {
                // Si falla la obtención del proceso actual, no bloquear la funcionalidad
                return;
            }

            const processArticleCountsById = new Map<number, number>();
            (existingOrderProcess?.rawMaterialsToUse ?? []).forEach((rawToUse: RawMaterialToUseDto) => {
                const articleId = rawToUse?.articleId ?? rawToUse?.article?.id;
                if (articleId && articleId > 0) processArticleCountsById.set(articleId, (processArticleCountsById.get(articleId) ?? 0) + 1);
            });

            // Comparar multiconjuntos: mismos ids y mismas cantidades
            let articleMultisetsMatch = true;
            if (tempArticleCountsById.size !== processArticleCountsById.size) {
                articleMultisetsMatch = false;
            } else {
                for (const [id, count] of tempArticleCountsById) {
                    if ((processArticleCountsById.get(id) ?? 0) !== count) {
                        articleMultisetsMatch = false;
                        break;
                    }
                }
            }

            if (!articleMultisetsMatch) {
                // No permitir precarga y forzar preload = false en todos los items
                this.allowPreload = false;
                this.items = this.items.map((tempItem: any) => ({ ...tempItem, preload: false }));
                this.message.warning('No se pueden precargar las materias primas porque no coincidene exactamente con las materias primas de la orden anterior.');
            }
        } catch (e: any) {
            console.warn('Error validando compatibilidad de materias primas temporales', e);
        }
    }

    cancel() {
        // Mostrar confirmación advirtiendo que al salir no se podrá precargar automáticamente luego
        const confirmModal = this.modalService.confirm({
            nzTitle: '¿Salir sin aplicar las materias primas?',
            nzContent: 'Si sales ahora no podrás volver a precargar automáticamente las materias primas y tendrás que hacerlo de forma manual. ¿Deseas salir de todos modos? ',
            nzOkText: 'Salir',
            nzCancelText: 'Volver',
            nzClosable: true,
            nzOnOk: async () => {
                if (!this.orderProcessId || this.orderProcessId <= 0) {
                    this.message.error('Id de proceso inválido');
                    return;
                }

                const decisions = this.items.map((item: any) => ({ rawMaterialUsedId: item.rawMaterialUsedId!, preload: false }));
                const userId = this.userLoggedService?.userLogged?.id;

                try {
                    this.loading = true;
                    const updatedProcess = await this.productionOrderProcessesService.applyLoadedRawMaterialsToProcess(this.orderProcessId, decisions, userId);
                    // Cerrar modal indicando que no hubo preloads activos
                    this.nzModalRef.close({ updatedProcess, anyPreloaded: false });
                } catch (e: any) {
                    this.message.error(`Error aplicando decisiones: ${e.message}`);
                } finally {
                    this.loading = false;
                }
            }
        });

    }

    async apply(): Promise<void> {
        if (!this.orderProcessId || this.orderProcessId <= 0) {
            this.message.error('Id de proceso inválido');
            return;
        }

        const decisions = this.items.map((item: any) => ({ rawMaterialUsedId: item.rawMaterialUsedId!, preload: this.allowPreload && !!item.preload }));
        const userId = this.userLoggedService?.userLogged?.id;

        try {
            this.loading = true;
            const updatedProcess = await this.productionOrderProcessesService.applyLoadedRawMaterialsToProcess(this.orderProcessId, decisions, userId);
            const anyPreloaded = decisions.some((d: any) => d.preload === true);
            // Devolver forma { updatedProcess, anyPreloaded } para que el padre decida mostrar toast
            this.nzModalRef.close({ updatedProcess, anyPreloaded });
        } catch (e: any) {
            this.message.error(`Error aplicando decisiones: ${e.message}`);
        } finally {
            this.loading = false;
        }
    }
}
